import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Reveal } from '../hooks/useReveal.tsx';
import { useMouseParallax } from '../hooks/useParallax.tsx';
import { TiltCard } from './TiltCard.tsx';
import { TechStackGrid } from './TechStackGrid.tsx';
import { MeshText } from './MeshText';

// Animated progress bar for languages
function AnimatedProgressBar({ 
  level, 
  delay, 
  inView 
}: { 
  level: number; 
  delay: number;
  inView: boolean;
}) {
  return (
    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
      <motion.div
        className="h-full bg-gradient-to-r from-white/40 via-white/60 to-white/40 rounded-full"
        initial={{ width: 0 }}
        animate={{ width: inView ? `${level}%` : 0 }}
        transition={{ 
          duration: 1.2, 
          delay: delay,
          ease: [0.4, 0, 0.2, 1]
        }}
      />
    </div>
  );
}

export function SkillsSection() {
  const languagesRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(languagesRef, { once: true, margin: '-100px' });
  const { x: mouseX, y: mouseY } = useMouseParallax(0.01);

  const languages = [
    { name: 'JavaScript', level: 85 },
    { name: 'Python', level: 70 },
    { name: 'TypeScript', level: 75 },
    { name: 'C++', level: 75 },
    { name: 'Java', level: 60 },
    { name: 'SQL', level: 80 },
  ];

  const currentlyLearning = ['Machine Learning', 'System Design', 'Cloud Architecture', 'Data Structures'];

  return (
    <section
      id="skills"
      className="flex flex-col px-5 sm:px-8 md:px-12 py-16 sm:py-20"
    >
      {/* Header */}
      <div className="flex flex-col gap-8 sm:flex-row sm:justify-between mb-12">
        <Reveal delay={120}>
          <motion.div 
            className="accent-badge px-3 py-1.5 rounded-r-md inline-block"
            style={{ x: mouseX, y: mouseY }}
          >
            <span className="mono-label-sm text-white/90">Skills & Tech</span>
          </motion.div>
        </Reveal>

        <Reveal delay={220} className="max-w-sm sm:text-right">
          <motion.p 
            className="text-lg sm:text-xl leading-relaxed text-white text-shadow"
            style={{ 
              x: mouseX ? mouseX.get() * -0.5 : 0, 
              y: mouseY ? mouseY.get() * -0.5 : 0 
            }}
          >
            Technologies and tools I work with to bring ideas to life.
          </motion.p>
        </Reveal>
      </div>

      {/* Content */}
      <div className="flex flex-col">
        {/* Interactive Tech Stack Grid */}
        <Reveal delay={180}>
          <div className="h-[50px] sm:h-[60px] lg:h-[70px] w-[260px] sm:w-[320px] lg:w-[380px] mb-8">
            <MeshText
              text="Tech Stack"
              color="#ffffff"
              fontFamily="Inter"
              fontWeight={500}
              fontSize={55}
              colorSplit={true}
              splitColors={['#61dafb', '#ff40c0']}
              force={18}
            />
          </div>
        </Reveal>

        <Reveal delay={250}>
          <div className="mb-16">
            <TechStackGrid 
              columns={7} 
              rows={3} 
              gap={8} 
              rounded={12}
              perspective={1200}
              rotateX={12}
              rotateY={0}
            />
          </div>
        </Reveal>

        {/* Bottom section: Languages and Currently Learning */}
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 mt-8">
          {/* Language Proficiency */}
          <div>
            <Reveal delay={400}>
              <div className="h-[35px] sm:h-[42px] lg:h-[50px] w-[200px] sm:w-[240px] lg:w-[280px] mb-6">
                <MeshText
                  text="Languages"
                  color="#ffffff"
                  fontFamily="Inter"
                  fontWeight={500}
                  fontSize={40}
                  colorSplit={true}
                  splitColors={['#40ff80', '#61dafb']}
                  force={16}
                />
              </div>
            </Reveal>

            <Reveal delay={450}>
              <TiltCard maxTilt={6} scale={1.01} glareMaxOpacity={0.1}>
                <div 
                  ref={languagesRef}
                  className="rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md p-6"
                >
                  <div className="space-y-5">
                    {languages.map((lang, index) => (
                      <motion.div 
                        key={lang.name}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                      >
                        <div className="flex justify-between items-center mb-2">
                          <div className="h-[18px] w-[120px]">
                            <MeshText
                              text={lang.name}
                              color="#ffffff"
                              fontFamily="Inter"
                              fontWeight={400}
                              fontSize={14}
                              colorSplit={true}
                              splitColors={['#61dafb', '#40ff80']}
                              force={10}
                            />
                          </div>
                          <motion.span 
                            className="mono-label text-white/50"
                            animate={isInView ? { opacity: [0, 1] } : {}}
                            transition={{ delay: 0.8 + index * 0.1 }}
                          >
                            {lang.level}%
                          </motion.span>
                        </div>
                        <AnimatedProgressBar 
                          level={lang.level} 
                          delay={0.5 + index * 0.1}
                          inView={isInView}
                        />
                      </motion.div>
                    ))}
                  </div>
                </div>
              </TiltCard>
            </Reveal>
          </div>

          {/* Currently Learning */}
          <div>
            <Reveal delay={500}>
              <div className="h-[35px] sm:h-[42px] lg:h-[50px] w-[320px] sm:w-[380px] lg:w-[440px] mb-6">
                <MeshText
                  text="Currently Exploring"
                  color="#ffffff"
                  fontFamily="Inter"
                  fontWeight={500}
                  fontSize={40}
                  colorSplit={true}
                  splitColors={['#ff40c0', '#40ff80']}
                  force={16}
                />
              </div>
            </Reveal>

            <Reveal delay={550}>
              <TiltCard maxTilt={10} scale={1.02}>
                <motion.div 
                  className="rounded-xl border border-white/15 bg-white/10 backdrop-blur-md p-6 h-full"
                  style={{
                    x: mouseX ? mouseX.get() * 0.4 : 0,
                    y: mouseY ? mouseY.get() * 0.4 : 0,
                  }}
                >
                  <div className="flex flex-wrap gap-3">
                    {currentlyLearning.map((item, index) => (
                      <motion.span
                        key={item}
                        className="px-4 py-2 text-sm rounded-full bg-white/5 text-white/70 border border-white/15 cursor-default"
                        initial={{ opacity: 0, scale: 0.8, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        transition={{ delay: 0.6 + index * 0.1 }}
                        whileHover={{ 
                          scale: 1.1,
                          backgroundColor: 'rgba(255,255,255,0.15)',
                          borderColor: 'rgba(255,255,255,0.3)',
                          color: 'rgba(255,255,255,0.95)',
                          boxShadow: '0 0 20px rgba(255,255,255,0.1)'
                        }}
                      >
                        {item}
                      </motion.span>
                    ))}
                  </div>
                  <p className="mt-6 text-sm text-white/50 leading-relaxed">
                    Always pushing boundaries and exploring new technologies to stay ahead in the ever-evolving tech landscape.
                  </p>
                </motion.div>
              </TiltCard>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
